import {isMac} from '@wandb/weave/common/util/browser';
import {Button} from '@wandb/weave/components/Button';
import React from 'react';
import TimeAgo from 'react-timeago';
import {twMerge} from 'tailwind-merge';

import {getIsPersonalWorkspace} from '../../../state/workspaces/types';
import {HOTKEYS, HOTKEYS_FALLBACK} from '../../../util/hotkeys';
import {KeyboardHintInTooltip} from '../../BetaFeatures/KeyboardHInt';
import {useUndoRedo} from '../hooks/useUndoRedo';
import {useViewBarContext} from '../NamedWorkspaceViewBarContext';
import {formatDate} from './NamedWorkspaceMenuItem';
import {useUndoRedoKeyboardShortcuts} from './useUndoRedoKeyboardShortcuts';

const undoHotkeys = isMac
  ? HOTKEYS.undoWorkspaceAction
  : HOTKEYS_FALLBACK.undoWorkspaceAction;
const redoHotkeys = isMac
  ? HOTKEYS.redoWorkspaceAction
  : HOTKEYS_FALLBACK.redoWorkspaceAction;

export const NamedWorkspaceUndoRedo = () => {
  const {currentView, undo, redo} = useUndoRedo();
  useUndoRedoKeyboardShortcuts();

  if (currentView == null) {
    return null;
  }

  return (
    <div className="flex gap-2">
      <Button
        data-test="nw-undo-button"
        icon="undo"
        variant="secondary"
        aria-label="Undo last action"
        disabled={!undo}
        onClick={() => undo?.(false)}
        tooltip={<KeyboardHintInTooltip text="Undo" keys={undoHotkeys} />}
        tooltipProps={{side: 'bottom'}}
      />
      <Button
        data-test="nw-redo-button"
        icon="redo"
        variant="secondary"
        aria-label="Redo last action"
        disabled={!redo}
        onClick={() => redo?.(false)}
        tooltip={<KeyboardHintInTooltip text="Redo" keys={redoHotkeys} />}
        tooltipProps={{side: 'bottom'}}
      />
    </div>
  );
};

export const NamedWorkspaceTimestamp = ({className}: {className: string}) => {
  const {currentView, currentWorkspaceNwId} = useViewBarContext();

  if (currentView == null) {
    return null;
  }

  if (currentView.saving) {
    return (
      <p
        className={twMerge(
          'text-base font-normal leading-[22px] text-moon-500',
          className
        )}>
        Saving...
      </p>
    );
  }

  const workspaceUpdatedAt = currentView.updatedAt;

  if (currentView.id == null || !workspaceUpdatedAt) {
    return (
      <p
        className={twMerge(
          'text-base font-normal leading-[22px] text-moon-500',
          className
        )}>
        Changes saved automatically
      </p>
    );
  }

  const isPersonalWorkspace = getIsPersonalWorkspace(currentWorkspaceNwId.id);

  if (!isPersonalWorkspace) {
    return (
      <p
        className={twMerge(
          'text-base font-normal leading-[22px] text-moon-500',
          className
        )}>
        Saved on {formatDate(currentView.updatedAt)}
      </p>
    );
  }

  return (
    <div
      className={twMerge(
        'text-base font-normal leading-[22px] text-moon-500',
        className
      )}>
      <p>
        Autosaved{' '}
        <span>
          <TimeAgo
            minPeriod={10}
            formatter={(value, unit, suffix, epochSeconds, nextFormatter) =>
              unit === 'second'
                ? 'just now'
                : nextFormatter!(value, unit, suffix, epochSeconds)
            }
            date={workspaceUpdatedAt}
          />
        </span>
      </p>
    </div>
  );
};
